import { AlertTriangle } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Cuán fuerte grita el aviso. Define el acento, no el comportamiento: los tres bloquean igual.
 */
export type SeveridadAviso = 'urgente' | 'importante' | 'informativo'

/**
 * Clases completas y literales por severidad. Tailwind escanea texto, así que armarlas
 * interpolando (`bg-${color}-100`) las deja fuera del bundle y el aviso sale sin color.
 */
const ACENTO: Record<SeveridadAviso, { circulo: string; icono: string; barra: string }> = {
  urgente: {
    circulo: 'bg-red-100 dark:bg-red-950',
    icono: 'text-red-600 dark:text-red-400',
    barra: 'bg-red-500',
  },
  importante: {
    circulo: 'bg-amber-100 dark:bg-amber-950',
    icono: 'text-amber-600 dark:text-amber-400',
    barra: 'bg-amber-500',
  },
  informativo: {
    circulo: 'bg-primary/10',
    icono: 'text-primary',
    barra: 'bg-primary',
  },
}

interface AvisoCriticoDialogProps {
  open: boolean
  /**
   * Única salida del aviso. No hay `onOpenChange`: el diálogo no se cierra con Escape, con
   * click afuera ni con una X, porque el punto es que nadie lo pueda descartar sin leerlo.
   */
  onEntendido: () => void
  titulo: string
  descripcion?: string
  severidad?: SeveridadAviso
  icono?: LucideIcon
  textoConfirmar?: string
  /** El cuerpo del aviso: lo que hay que ver. */
  children?: ReactNode
  /** Acciones que NO cierran el aviso, al lado del botón de confirmar. */
  acciones?: ReactNode
}

/**
 * Aviso que ocupa el centro de la pantalla y no se va solo.
 *
 * Es el escalón por encima del toast, que se desvanece a los segundos y se pierde si el
 * usuario no estaba mirando. Reservarlo para lo que no se puede pasar por alto: usarlo de
 * más entrena a cerrarlo sin leer y lo vuelve tan invisible como el toast.
 */
export function AvisoCriticoDialog({
  open,
  onEntendido,
  titulo,
  descripcion,
  severidad = 'importante',
  icono: Icono = AlertTriangle,
  textoConfirmar = 'Entendido',
  children,
  acciones,
}: AvisoCriticoDialogProps) {
  const acento = ACENTO[severidad]

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        className="max-w-md overflow-hidden p-0 gap-0"
        // Sin esto, Escape lo cierra y el aviso deja de ser un aviso.
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className={`h-1.5 w-full ${acento.barra}`} />

        <div className="flex flex-col items-center gap-4 px-6 pt-6 pb-2 text-center">
          <div className={`flex h-14 w-14 items-center justify-center rounded-full ${acento.circulo}`}>
            <Icono className={`h-7 w-7 ${acento.icono}`} aria-hidden="true" />
          </div>

          <div className="space-y-1.5">
            <AlertDialogTitle className="text-lg font-semibold">{titulo}</AlertDialogTitle>
            {descripcion && (
              <AlertDialogDescription className="text-sm">{descripcion}</AlertDialogDescription>
            )}
          </div>
        </div>

        {children && <div className="px-6 py-2">{children}</div>}

        <div className="flex flex-col-reverse gap-2 px-6 pb-6 pt-4 sm:flex-row sm:justify-center">
          {acciones}
          <AlertDialogAction onClick={onEntendido} className="sm:min-w-32">
            {textoConfirmar}
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
